
import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';

import * as moment from 'moment';
import * as _ from 'lodash';
import {map} from 'rxjs';
import {UrlService} from '../url.service';

@Injectable()
export class AssessmentService {
    private BASE_URL: string;

    public languageConfig = {
        language: {
            '': {
                flagGifUrl: 'images/flags/gif/us.gif',
                name: 'English'
            },
            'ES': {
                flagGifUrl: 'images/flags/gif/es.gif',
                name: 'Spanish'
            },
            'SP': {
                flagGifUrl: 'images/flags/gif/es.gif',
                name: 'Spanish'
            }
        }
    };

    public assessmentStatusTypes = [
        {id: 4, name: 'Assigned'},
        {id: 2, name: 'Submitted'},
        {id: 5, name: 'Approved'},
        {id: 6, name: 'Returned'},
        {id: 8, name: 'Deleted'}
    ];

    private sequentialApprovalParams: HttpParams = null;

    constructor(private http: HttpClient,
                private urlService: UrlService) {
        this.BASE_URL = this.urlService.getBackendUrl() + 'data/v2/assessments';
    }

    public getAssessment(assessmentId: string, languageCode) {
        let params = new HttpParams();
        if (languageCode) {
            params = params.set('languageCode', languageCode);
        }

        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/' + assessmentId, requestOptions);
    }

    public createAssessment(assessment, languageCode) {
        let params = new HttpParams();
        if (languageCode) {
            params = params.set('languageCode', languageCode);
        }

        const requestOptions = {
            params: params
        }

        return this.http.post(this.BASE_URL, assessment, requestOptions);
    }

    public createAssessmentByClientId(instrumentType, clientId, languageCode) {
        let params = new HttpParams();
        if (languageCode) {
            params = params.set('languageCode', languageCode);
        }

        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/create/' + instrumentType + '/' + clientId, requestOptions);
    }

    public updateAssessment(assessment, assessmentId, languageCode) {
        let params = new HttpParams();
        if (languageCode) {
            params = params.set('languageCode', languageCode);
        }

        const requestOptions = {
            params: params
        }

        return this.http.put(this.BASE_URL + '/' + assessmentId, assessment, requestOptions);
    }

    loadAssessment(assessmentId, languageCode) {
        let params = new HttpParams();
        if (languageCode) {
            params = params.set('languageCode', languageCode);
        }

        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/' + assessmentId, requestOptions);
    }

    loadAssessmentInfo(assessmentId) {
        return this.http.get(this.BASE_URL + '/' + assessmentId + '/info');
    }

    public deleteAssessments(clientId, assessmentsIds) {
        let params = new HttpParams();
        params = params.set('clientId', clientId);
        params = params.set('assessmentsIds', JSON.stringify(assessmentsIds));

        const requestOptions = {
            params: params
        }

        return this.http.delete(this.BASE_URL, requestOptions);
    }

    public getAssessments(params) {
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL, requestOptions);
    }

    public getAssessmentsFlat(params) {
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/flat', requestOptions);
    }

    validateAssessment(assessment) {
        return this.http.put(this.BASE_URL + '/' + assessment.id + '/validate', assessment);
    }

    submitAssessment(assessment) {
        return this.http.put(this.BASE_URL + '/' + assessment.id + '/submit', assessment);
    }

    recallAssessment(assessment) {
        return this.http.put(this.BASE_URL + '/' + assessment.id + '/recall', assessment);
    }

    submitAndApproveAssessment(assessment) {
        return this.http.put(this.BASE_URL + '/' + assessment.id + '/submitAndApprove', assessment);
    }

    rejectAssessment(assessment, rejectNote) {
        let params = new HttpParams();
        params = params.set('rejectNote', rejectNote);

        const requestOptions = {
            params: params
        }

        return this.http.put(this.BASE_URL + '/' + assessment.id + '/reject', assessment, requestOptions);
    }

    deleteAssessment(assessment) {
        return this.http.delete(this.BASE_URL + '/' + assessment.id);
    }

    getAssessmentSurroundingIds(clientId, ouId, instrumentBaseType, instrumentType, assessmentDate, reasonForAssessment, assessmentId) {
        let params = new HttpParams();
        params = params.set('clientId', clientId);
        params = params.set('ouId', ouId);
        params = params.set('instrumentBaseType', instrumentBaseType);
        params = params.set('instrumentType', instrumentType);
        params = params.set('assessmentDate', assessmentDate);
        params = params.set('reasonForAssessment', reasonForAssessment);
        params = params.set('assessmentId', assessmentId);

        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/surroundingIds', requestOptions);
    }

    getPreviousAssessmentId(assessmentId) {
        let params = new HttpParams();
        params = params.set('assessmentId', assessmentId);

        return this.http.get(this.BASE_URL + '/previousId', { params });
    }

    isAssessmentCategoryVisible(category, visibilityMap, assessment, assessmentDateObj) {
        if ((!visibilityMap)
            || (!assessment)
            || (!assessment.type)
            || (!visibilityMap[assessment.type])
            || (!visibilityMap[assessment.type].domains[category.urlPath])) {
            return true;
        }

        const effDate = (assessmentDateObj && assessmentDateObj.date) ? assessmentDateObj.date : assessment.assessmentDate;

        let evalString = visibilityMap[assessment.type].domains[category.urlPath];
        evalString = evalString.replace(new RegExp('CLIENT.AGE', 'g'), moment(effDate ? moment(effDate) : moment())
            .diff(assessment.client.dob, 'years'));
        evalString = evalString.replace(new RegExp('ORGUNIT.CODE', 'g'), assessment.orgUnit != null ? assessment.orgUnit.code : '');

        assessment.categories.forEach((category) => {
            category.questions.forEach((question) => {
                const matchingString = '__' + question.identifier;
                const answerValue = question.a[0];

                if (answerValue) {
                    evalString = evalString.replace(new RegExp(matchingString, 'g'), answerValue);
                }
            });
        });

        return this.evaluateFunction(evalString);

    }

    isAssessmentQuestionVisible(question, visibilityMap, assessment, assessmentDateObj) {
        if ((!visibilityMap)
            || (!assessment)
            || (!visibilityMap[assessment.type])
            || (!visibilityMap[assessment.type].questions[question.identifier])) {
            return true;
        }

        const effDate = (assessmentDateObj && assessmentDateObj.date) ? assessmentDateObj.date : assessment.assessmentDate;

        let evalString = visibilityMap[assessment.type].questions[question.identifier];
        evalString = evalString.replace(new RegExp('CLIENT.AGE', 'g'), moment(effDate ? moment(effDate) : moment())
            .diff(assessment.client.dob, 'years'));
        evalString = evalString.replace(new RegExp('ORGUNIT.CODE', 'g'), assessment.orgUnit != null ? assessment.orgUnit.code : '');

        assessment.categories.forEach((category) => {
            category.questions.forEach((question) => {
                const matchingString = '__' + question.identifier;
                const answerValue = question.a[0];

                if (answerValue) {
                    evalString = evalString.replace(new RegExp(matchingString, 'g'), answerValue);
                }
            });
        });

        return this.evaluateFunction(evalString);
    }

    evaluateFunction(functionBody: string): boolean {
        try {
            if (!functionBody || functionBody.trim() === '') {
                return true; // Default to visible if no condition
            }

            // Wrap the expression in a return statement
            const functionWithReturn = `return ${functionBody}`;
            const evaluateFunction = Function(functionWithReturn);
            const result = evaluateFunction();

            // Ensure we return a boolean
            return Boolean(result);
        } catch (e) {
            console.error('Error evaluating function:', e);
            console.error('Function body:', functionBody);
            return true; // Default to visible on error
        }
    }

    getAssessmentStatusTypes() {
        return this.assessmentStatusTypes;
    }

    registerSequentialBatchApprovalInfo(filtersJson, selections) {
        const filters = JSON.parse(filtersJson);
        // filters.push({'property': 'cansAssessmentCompletionCode.id', 'value': '2'});
        if (selections && selections.length > 0) {
            const sequentialApprovalSelections = [];
            selections.forEach((selection) => {
                sequentialApprovalSelections.push(selection.id);
            });
            filters.push({'property': 'idsList', 'value': sequentialApprovalSelections.toString()});
        }

        this.sequentialApprovalParams = new HttpParams();
        this.sequentialApprovalParams = this.sequentialApprovalParams.set('start', 0);
        this.sequentialApprovalParams = this.sequentialApprovalParams.set('limit', 1);
        this.sequentialApprovalParams = this.sequentialApprovalParams.set('sortField', 'assessmentDate');
        this.sequentialApprovalParams = this.sequentialApprovalParams.set('sortOrder', 'asc');
        this.sequentialApprovalParams = this.sequentialApprovalParams.set('filter', JSON.stringify(filters));
        // Not all instruments need to be approved, only include relevant ones
        this.sequentialApprovalParams = this.sequentialApprovalParams.set('includeInstruments', JSON.stringify(['CANS', 'ANSA', 'FAST', 'FANS']));
        // Always exclude the CAREGIVERs
        this.sequentialApprovalParams = this.sequentialApprovalParams.set('excludeInstruments', JSON.stringify(['_CAREGIVER']));

        // this.sequentialApprovalParams = {
        //     start: 0,
        //     limit: 1,
        //     sortField: 'assessmentDate',
        //     sortOrder: 'asc',
        //     filter: JSON.stringify(filters),
        //     clientId: null,
        //     includeInstruments: JSON.stringify(['CANS', 'ANSA', 'FAST', 'FANS']),
        //     excludeInstruments: JSON.stringify(['_CAREGIVER']), // We always exclude CAREGIVER assessments by default
        //     evaluation: true
        // };
    }

    getNextSequentialBatchApprovalAssessmentInfo() {
        const requestOptions = {
            params: this.sequentialApprovalParams
        }
        return this.http.get(this.BASE_URL, requestOptions);
    }

    getSequentialBatchApprovalParams() {
        return this.sequentialApprovalParams;
    }

    getAlgorithmScoresForAssessment(assessmentId) {
        return this.http.get(this.BASE_URL + '/' + assessmentId + '/' + 'algorithmsresults');
    }

    getAlgorithmScoresForClient(clientId) {
        return this.http.get(this.BASE_URL + '/' + clientId + '/' + 'allalgorithmsresults');
    }

    getRestrictedAlgorithmScoresForClient(algorithmsDesired, clientPlacementId) {
        return this.http.get(this.BASE_URL + '/' + clientPlacementId + '/' + 'allalgorithmsresults').pipe(
            map(results => {
                const filtered = _.filter(results, (result: any) => {
                    return _.filter(algorithmsDesired, result.algo)
                });
                return filtered;
            })
        );
    }

    applyAssessmentLogic(assessment, assessmentDate) {
        // Build the questions/answers map
        const valuesMap = {};
        _.each(assessment.categories, function (category) {
            _.each(category.questions, function (question) {
                valuesMap[question.identifier] = question.a[0];
            });
        });

        if ((assessment.client) && (assessment.client.dob)) {
            const d = assessmentDate ? assessmentDate : assessment.assessmentDate;
            valuesMap['client_age'] = Math.round(moment.duration(moment(d).diff(moment(assessment.client.dob))).asMonths());
        }

        return this.http.put(this.BASE_URL + '/applyLogic/' + assessment.instrumentId + '/' + assessment.type, valuesMap);
    }

    countBeforeForEpisode(clientId, ouId, baseType, type, date) {
        return this.http.get(this.BASE_URL + '/countBeforeForEpisode/' + clientId + '/' + ouId + '/' + baseType + '/' + type + '/' + date);
    }

    getLanguageConfig() {
        return this.languageConfig;
    }

    getAssessmentUrl(baseType, id, groupId, sequentialApproval) {
        switch (baseType) {
            case 'CANS':
            case 'ANSA':
                if (groupId) {
                    return '/data_entry/assessmentc/' + id + '/' + groupId + (sequentialApproval ? '/approveSequential' : '');
                } else {
                    return '/data_entry/assessmentc/' + id + (sequentialApproval ? '/approveSequential' : '');
                }
                break;
            case 'ACE':
            case 'ASQ':
            case 'BRIEFP':
            case 'DAYC2':
            case 'ECBI':
            case 'PDMS2':
            case 'PSISF':
            case 'SPMP':
            case 'TSCYC':
                return '/data_entry/assessmentad/' + id;
            case 'PSC':
            case 'PHQ':
            case 'GAD':
            case 'CSSRS':
            case 'ASAM':
            case 'ADVERSE_CHILDHOOD_EXPERIENCES':
            case 'M3':
            case 'MSA':
            case 'CSA':
            case 'EDEQ':
            case 'EDEQA':
                return '/data_entry/assessmentpsc/' + id;
            default:
                return '/data_entry/assessment/' + id + (sequentialApproval ? '/approveSequential' : '');
        }
    }

    getAssessmentMatrix(params) {
        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/matrix', requestOptions);
    }

    getAssessmentCount(params) {
        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/assessmentCount', requestOptions);
    }

    getQuestionByIdentifier(assessment, questionIdentifier) {
        for (let i = 0; i < assessment.categories.length; ++i) {
            const category = assessment.categories[i];
            for (let j = 0; j < category.questions.length; ++j) {
                const question = category.questions[j];
                if (questionIdentifier === question.identifier) {
                    return question;
                }
            }
        }
        return {};
    }

    getDistinctInstrumentTypes(assessments) {
        const a = [];
        assessments.forEach((assessment) => {
            if (a.indexOf(assessment.type) < 0) {
                a.push(assessment.type);
            }
        });
        return a;
    }
}
